import types from '../actions/types';
import { DEFAULT_PORT_SOURCE } from '../actions/bloquesAction';

/**
 * One agent instance per tab reports ports, and each one only sees the devices of
 * its own transport (serial through the desktop agent, Web Serial, WebUSB). Their
 * lists are kept apart by source and merged into a single one, so an agent can no
 * longer wipe the ports another agent reported. Duplicates across sources are
 * dropped by port name, which is what identifies a port everywhere in the app.
 */
const mergeBySource = (bySource, keyOf) => {
  const merged = [];
  const seen = {};
  Object.keys(bySource).forEach((source) => {
    (bySource[source] || []).forEach((port) => {
      const key = keyOf(port);
      if (!seen[key]) {
        seen[key] = true;
        merged.push(port);
      }
    });
  });
  return merged;
};

const portName = (port) => port;
const agentPortName = (port) => (port ? port.Name : '');

const NO_PORTS = [];

/**
 * The ports of a single agent: the boards that one tab can send a hex to. Until
 * that agent reports for the first time its list is empty, which is the truth for
 * that tab. A caller with no agent of its own gets the merged list instead.
 */
const listOfSource = (bySource, merged, source) => {
  if (!source) return merged;
  return (bySource && bySource[source]) || NO_PORTS;
};

export const portsOfSource = (bloques, source) => listOfSource(bloques.portsBySource, bloques.ports, source);
export const agentPortsOfSource = (bloques, source) => listOfSource(bloques.agentPortsBySource, bloques.agentPorts, source);

export const bloquesInitialState = {
  statusPorts: '',
  statusMsg: '',
  statusMonitorMsg: '',
  statusState: '',
  statusWifi: '',
  wifiList: [],
  ports: [],
  agentPorts: [],
  // Ports as reported by each agent, keyed by source. The two lists above stay
  // as they were: the flat, merged lists that the whole app consumes.
  portsBySource: {},
  agentPortsBySource: {},
  msgs: [],
  monitorMsgs: [],
  state: {
    msgs: [],
    ebBuild: 'ERROR',
    agent: '',
    progress: 0,
  },
};

export default function (state = bloquesInitialState, action) {
  switch (action.type) {
    case types.GET_PORTS:
    {
      const source = action.payload.source || DEFAULT_PORT_SOURCE;
      // A copy: the agents mutate their own list in place, and a stored reference
      // would neither change between dispatches nor be safe to keep.
      const portsBySource = Object.assign({}, state.portsBySource, { [source]: (action.payload.ports || []).slice() });
      return Object.assign({}, state, {
        statusPorts: action.payload.statusPorts,
        portsBySource,
        ports: mergeBySource(portsBySource, portName),
      });
    }
    case types.GET_AGENT_PORTS:
    {
      const source = action.payload.source || DEFAULT_PORT_SOURCE;
      const agentPortsBySource = Object.assign({}, state.agentPortsBySource, { [source]: (action.payload.ports || []).slice() });
      return Object.assign({}, state, {
        agentPortsBySource,
        agentPorts: mergeBySource(agentPortsBySource, agentPortName),
      });
    }
    case types.GET_NEW_MESSAGE:
      const msgs = state.msgs; // eslint-disable-line
      msgs.push(action.payload.msg);
      return Object.assign({}, state, { statusMsg: action.payload.statusMsg, msgs });
    case types.GET_NEW_STATE:
      switch (action.payload.stateMsg) {
        case '#### UPLOAD PROGRESS':
          state.state.progress = action.payload.progress || 0; // eslint-disable-line
          break;
        case '#### BOARD CONNECTED':
          state.state.agent = 'GOOD'; // eslint-disable-line
          break;
        case '#### BOARD NOT CONNECTED':
          state.state.agent = 'ERROR'; // eslint-disable-line
          break;
        case '#### PORT CONNECTED':
          state.state.ebBuild = 'GOOD'; // eslint-disable-line
          break;
        case '#### PORT NOT CONNECTED':
          state.state.ebBuild = 'ERROR'; // eslint-disable-line
          break;
        default:
          break;
      }
      state.state.msgs.push(action.payload.stateMsg);
      return Object.assign({}, state, { statusState: action.payload.statusState, state: state.state });
    case types.GET_NEW_MONITOR_MESSAGE:
    {
      const monitorMsgs = action.payload.monitorMsgs;
      return Object.assign({}, state, { statusMonitorMsg: action.payload.statusMonitorMsg, monitorMsgs });
    }
    case types.BLOQUES_RESET_STATUS:
      return Object.assign({}, state, action.payload);
    default:
      return state;
  }
}
